/**
 * Format ISO 8601 datetime string into a human-friendly format.
 *
 * @param {string} isoString - The ISO 8601 datetime string.
 * @param {object} options - Formatting options.
 * @param {string} [options.format="shortDate"] - Format type ("shortDate", "dateTime", "longDate", "longDateTime", "timeOnly", "dayLongDate").
 * @param {boolean} [options.useLocalTime=false] - Whether to convert to local time (default is UTC).
 * @param {boolean} [options.includeSeconds=false] - Whether to include seconds in the time output.
 * @param {string} [options.locale="en-GB"] - Locale code for formatting (e.g., "en-GB", "en-US", "de-DE").
 * @returns {string} - Formatted date string.
 */
export function formatISODate(isoString, options = {}) {
    const {
        format = "shortDate",
        useLocalTime = false,
        includeSeconds = false,
        locale = "en-GB",
        backupValue = null
    } = options;

    try {
        // Parse the ISO string
        const date = new Date(isoString);

        // Define format options based on `format` and `includeSeconds`
        let formatOptions;
        switch (format) {
            case "dateTime":
                formatOptions = {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: includeSeconds ? "2-digit" : undefined
                };
                break;
            case "longDate":
                formatOptions = {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                };
                break;
            case "longDateTime":
                formatOptions = {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: includeSeconds ? "2-digit" : undefined
                };
                break;
            case "timeOnly":
                formatOptions = {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: includeSeconds ? "2-digit" : undefined
                };
                break;
            case "dayLongDate":
                const day = new Intl.DateTimeFormat(locale, { weekday: "long" }).format(date);
                const datePart = new Intl.DateTimeFormat(locale, {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                }).format(date);
                return `${day} ${datePart}`;
            case "shortDate":
            default:
                formatOptions = {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit"
                };
        }

        // Set timeZone option only when useLocalTime is false (for UTC display)
        if (!useLocalTime) {
            formatOptions.timeZone = "UTC";
        }

        // Create a date formatter with the options, using the provided locale
        return new Intl.DateTimeFormat(locale, formatOptions).format(date);
    }
    catch(error)
    {
        if(backupValue)
            return backupValue;
        else
            throw error;
    }
}

export function formatISOTime(isoString, options = {}) {
    const {
        format = "timeOnly",
        useLocalTime = true,
        includeSeconds = false,
        locale = "en-GB",
        backupValue = '-'
    } = options;

    return formatISODate(isoString, {
        format,
        useLocalTime,
        includeSeconds,
        locale,
        backupValue
    });
}

/**
 * Converts any Date object to its UTC equivalent.
 * @param {Date} date - The original Date object.
 * @returns {Date} A new Date object representing the UTC date.
 */
export function toUTC(date) {
    if (!(date instanceof Date)) {
        throw new Error('Invalid date object provided.');
    }

    // Construct a new Date object in UTC by extracting the UTC components
    return new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds(),
        date.getUTCMilliseconds()
    ));
}

/**
 * Converts any Date object to its UTC equivalent.
 * @param {string | Date} dateStr - The string representation of Date.
 * @returns {Date} A new Date object representing the UTC date.
 */
export function toUTCFromString(dateStr) {
    if(dateStr instanceof Date)
        return toUTC(dateStr)

    if (!(typeof dateStr === 'string' || dateStr instanceof String)) {
        throw new Error('Invalid date object provided.');
    }

    return toUTC(new Date(dateStr));
}

export function durationBetween(str1, str2) {
    // Parse the ISO-8601 strings into Date objects
    const date1 = new Date(str1);
    const date2 = new Date(str2);

    // Validate date parsing
    if (isNaN(date1) || isNaN(date2)) {
        throw new Error("Invalid ISO-8601 date string provided.");
    }

    // Calculate the difference in milliseconds
    const diffMilliseconds = Math.abs(date1 - date2);

    // Convert milliseconds to hours
    const diffHours = diffMilliseconds / (1000 * 60 * 60);

    return Math.abs(diffHours);
}

export function differenceInSecondsDates(date1, date2) {
    // Parse the input date strings into Date objects
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    // Calculate the difference in milliseconds
    const diffMilliseconds = Math.abs(d1 - d2);

    // Convert milliseconds to seconds
    return diffMilliseconds / 1000;
}

export function isCurrentTimeBetween(startTimeString, endTimeString) {
    // Parse the input date-time strings into Date objects
    const startTime = new Date(startTimeString);
    const endTime = new Date(endTimeString);

    // Get the current time
    const currentTime = new Date();

    // Check if current time is between start time and end time
    return currentTime >= startTime && currentTime <= endTime;
}

export function formatDateForDateInput(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function getAllDaysBetween(startDate, endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const dates = [];

    if (start > end) {
        throw new Error("Start date must be before end date");
    }

    let current = new Date(start);
    while (current <= end) {
        dates.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }

    return dates;
}

export const parseTimeSpan = (timeSpanStr) => {
    const parts = timeSpanStr.split(":"); // Split by ':'

    if (parts.length < 3) {
        return null;
    }

    let days = 0;
    let hours, minutes, seconds;

    if (parts[0].includes(".")) {
        // Extract days if present (e.g., "1825.00:00:00")
        const dayHourParts = parts[0].split(".");
        days = parseInt(dayHourParts[0], 10);
        hours = parseInt(dayHourParts[1], 10);
    } else {
        hours = parseInt(parts[0], 10);
    }

    minutes = parseInt(parts[1], 10);
    seconds = parseInt(parts[2], 10);

    // Convert everything to total seconds
    const totalSeconds = (days * 86400) + (hours * 3600) + (minutes * 60) + seconds;

    return {
        days,
        hours,
        minutes,
        seconds,
        totalSeconds,
        totalMinutes: totalSeconds / 60,
        totalHours: totalSeconds / 3600,
        totalDays: totalSeconds / 86400,
    };
};

export const formatDurationFromTimeSpan = (duration, t) => {
    const dayStr = duration?.days !== 1 ? `${t('global.time.units.plural.day')}` : t('global.time.units.singular.day');
    const hourStr = duration?.hours !== 1 ? `${t('global.time.units.plural.hour')}` : t('global.time.units.singular.hour');
    const minuteStr = duration?.minutes !== 1 ? `${t('global.time.units.plural.minute')}` : t('global.time.units.singular.minute');
    const secondStr = duration?.seconds !== 1 ? `${t('global.time.units.plural.second')}` : t('global.time.units.singular.second');
    
    let parts = [];
    if (duration?.days > 0) parts.push(`${duration.days} ${dayStr}`);
    if (duration?.hours > 0) parts.push(`${duration.hours} ${hourStr}`);
    if (duration?.minutes > 0) parts.push(`${duration.minutes} ${minuteStr}`);
    if (duration?.seconds > 0) parts.push(`${duration.seconds} ${secondStr}`);

    return parts.length ? parts.join(" ") : "-";
};

export const addHoursToIsoString = (isoString, hours) => {
    const date = new Date(isoString);
    date.setHours(date.getHours() + hours);
    return date.toISOString();
}